import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { LogIn, Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { useAuthStore } from "@/stores/auth.store";
import { useToast } from "@/stores/ui.store";
import { authenticateWithJest } from "@/lib/jest-auth";
import { awaitRegisteredSignedPlayer } from "@/lib/jest-registration";
import { isRegisteredUser } from "@/lib/jest-guest";
import { scheduleAfterRegistration } from "@/lib/notifications";

export default function LoginPage() {
  const router = useRouter();
  const { user, isAuthenticated, setAuth, setUser } = useAuthStore();
  const { error: showError, success: showSuccess } = useToast();

  const [isLoading, setIsLoading] = useState(false);

  const sdkReady = typeof window !== "undefined" && window.JestSDK != null;

  useEffect(() => {
    if (isAuthenticated && user && isRegisteredUser(user)) {
      router.push("/");
    }
  }, [isAuthenticated, user, router]);

  const handleJestRegister = async () => {
    setIsLoading(true);
    try {
      if (!window.JestSDK) {
        throw new Error("JestSDK unavailable");
      }

      try {
        await window.JestSDK.showRegistrationOverlay();
      } catch {
        // dismissed overlay — not an error
        return;
      }

      // Registration is optional and must never block. Give the SDK a brief
      // window to reflect a just-completed registration (the overlay can resolve
      // before getPlayerSigned() reports registered=true), then re-auth and
      // continue regardless. A player who didn't finish registering — e.g. the
      // simulator's mocked host, which can't complete the overlay — simply stays
      // a full-access guest instead of hitting a dead end.
      const signed = await awaitRegisteredSignedPlayer(window.JestSDK, {
        attempts: 4,
        delayMs: 400,
      });

      const auth = await authenticateWithJest(window.JestSDK, {
        playerSigned: signed.playerSigned,
      });
      setAuth(auth.accessToken, auth.sessionId);
      if (auth.user) {
        setUser(auth.user);
      }

      if (isRegisteredUser(auth.user)) {
        showSuccess(
          "Account Created!",
          `Welcome, ${auth.user?.username ?? "player"}!`,
        );
        void scheduleAfterRegistration(window.JestSDK);
      }

      router.push("/");
    } catch (err) {
      showError(
        "Error",
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleJestSignIn = async () => {
    setIsLoading(true);
    try {
      if (!window.JestSDK) {
        throw new Error("JestSDK unavailable");
      }
      // autoLoginReminders stays false — true renders Jest's SMS-consent widget
      // pinned to the bottom of the screen, which overlaps the BottomTabBar. See
      // main.tsx boot init for the full rationale.
      await window.JestSDK.init({
        autoLoginReminders: false,
      });

      const auth = await authenticateWithJest(window.JestSDK);

      setAuth(auth.accessToken, auth.sessionId);
      if (auth.user) {
        setUser(auth.user);
      }

      showSuccess(
        "Welcome!",
        `Signed in as ${auth.user?.username ?? auth.user?.id ?? "Jest player"}`,
      );

      void scheduleAfterRegistration(window.JestSDK);
      router.push("/");
    } catch (err) {
      showError(
        "Error",
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isAuthenticated && user && !isRegisteredUser(user)) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
        <Card variant="parchment" className="w-full max-w-md">
          <CardHeader padding="lg" className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-xl bg-parchment-dark border border-parchment-border flex items-center justify-center">
              <UserPlus className="h-6 w-6 text-surface/60" />
            </div>
            <CardTitle className="text-2xl font-serif text-surface">
              Create Your Account
            </CardTitle>
            <CardDescription className="text-surface/60">
              Register on Jest to save your progress and unlock notifications
            </CardDescription>
          </CardHeader>

          <CardContent padding="lg">
            <Button
              type="button"
              onClick={handleJestRegister}
              className="w-full"
              size="lg"
              disabled={!sdkReady || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Creating account…
                </>
              ) : (
                <>
                  <UserPlus className="h-5 w-5" />
                  Register on Jest
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <Card variant="parchment" className="w-full max-w-md">
        <CardHeader padding="lg" className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-xl bg-parchment-dark border border-parchment-border flex items-center justify-center">
            <LogIn className="h-6 w-6 text-surface/60" />
          </div>
          <CardTitle className="text-2xl font-serif text-surface">
            Welcome to FantasyPRIME
          </CardTitle>
          <CardDescription className="text-surface/60">
            Sign in with your Jest account to continue
          </CardDescription>
        </CardHeader>

        <CardContent padding="lg">
          <Button
            type="button"
            onClick={handleJestSignIn}
            className="w-full"
            size="lg"
            disabled={!sdkReady || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Signing in…
              </>
            ) : (
              <>
                <LogIn className="h-5 w-5" />
                Sign in with Jest
              </>
            )}
          </Button>

          {!sdkReady && (
            <p className="mt-4 text-center text-sm text-surface/60">
              JestSDK not available — running in degraded dev mode.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
